import { useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import Pill from 'honeydew/Pill';
import useStickyShopNavigation, { STICKY_SHOP_NAVIGATION_STYLES } from 'hooks/useStickyShopNavigation';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { scrollToL2AisleElement } from 'utils/aislesUtils';
import { trackAisleNavigation } from 'utils/analyticsUtils';

export function AisleSubcategoryNavigationWrapper({ children }) {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const stickyShopNavigation = useStickyShopNavigation();

    return (
        <div
            className={clsx('bg-malt if:bg-oat md:mx-0 md:bg-white md:px-0 md:if:bg-white', {
                [`sticky top-[90px] z-10 ${STICKY_SHOP_NAVIGATION_STYLES.duration}`]:
                    stickyShopNavigation.hideSiteNav && mobile && !window.isNativeApp,
                [`sticky top-[226px] z-10 ${STICKY_SHOP_NAVIGATION_STYLES.duration}`]:
                    !stickyShopNavigation.hideSiteNav && mobile && !window.isNativeApp,
                'sticky top-[146px] z-10': window.isNativeApp,
            })}
        >
            <nav className={clsx('flex overflow-auto px-10 py-[8px]')}>{children}</nav>
        </div>
    );
}

AisleSubcategoryNavigationWrapper.propTypes = {
    children: PropTypes.node.isRequired,
};

function AisleSubcategoryNavigation({ categories, currentCategoryId, setCurrentCategoryId }) {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    useEffect(() => {
        if (mobile) {
            const element = document.querySelector(`#nav-${currentCategoryId}`);
            element?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' });
        }
    }, [currentCategoryId]);

    return (
        <AisleSubcategoryNavigationWrapper>
            <Pill
                as="button"
                id="nav-aisle-all"
                onClick={() => {
                    window.scrollTo({ top: 0 });
                    setCurrentCategoryId('aisle-all');
                }}
                variant={currentCategoryId === 'aisle-all' ? 'active' : 'default'}
            >
                All
            </Pill>
            {categories?.map(({ id, name }) => (
                <span id={`nav-aisle-${id}`} className="pl-10" key={id}>
                    <Pill
                        as="button"
                        onClick={() => {
                            scrollToL2AisleElement({ mobile, selector: `#aisle-${id}` });
                            setCurrentCategoryId(`aisle-${id}`);
                            trackAisleNavigation({
                                aisleName: name,
                                level: 2,
                                mobile,
                                uiElement: 'L2',
                            });
                        }}
                        variant={currentCategoryId === `aisle-${id}` ? 'active' : 'default'}
                    >
                        {name}
                    </Pill>
                </span>
            ))}
        </AisleSubcategoryNavigationWrapper>
    );
}

AisleSubcategoryNavigation.propTypes = {
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    currentCategoryId: PropTypes.string.isRequired,
    setCurrentCategoryId: PropTypes.func.isRequired,
};

export default AisleSubcategoryNavigation;
